/**
 * PC-v2.2-C2.1 — PERIODOS DE LA ANALÍTICA (Calidad interna y Calidad del portal)
 *
 * Una sola definición de «Este mes», «Mes anterior», «Últimos 90 días», «Este año» y «Personalizado» para todas las
 * pantallas que la usan, con la fecha LOCAL de hoy (`hoyLocal`). El periodo vive en la URL (`periodo`, y `desde` /
 * `hasta` sólo si es personalizado): lo que llegue de la URL se valida aquí antes de pedir nada al servidor, y un
 * periodo mal formado nunca se envía. «Últimos 30 días» se mantiene: es el que ya usaban esas pantallas.
 *
 * El Calendario operativo NO usa esto: su ventana sigue siendo de 31 días.
 */
import { hoyLocal } from './componentes/base.tsx';

export type ClavePeriodo = 'mes' | 'mes_anterior' | '30' | '90' | 'anio' | 'personalizado';

export const OPCIONES_PERIODO: readonly (readonly [ClavePeriodo, string])[] = [
  ['mes', 'Este mes'],
  ['mes_anterior', 'Mes anterior'],
  ['30', 'Últimos 30 días'],
  ['90', 'Últimos 90 días'],
  ['anio', 'Este año'],
  ['personalizado', 'Personalizado'],
];

/** El límite de la evolución (G1–G6) y del portal: lo mismo que el servidor (`MAX_DIAS_EVOLUCION`). */
export const MAX_DIAS_ANALITICA = 366;

export function esClavePeriodo(x: string): x is ClavePeriodo {
  return OPCIONES_PERIODO.some(([k]) => k === x);
}

const DIA = 86_400_000;
/** Una fecha `AAAA-MM-DD` que existe (no 30 de febrero). */
export function esFechaValida(x: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(x)) return false;
  const t = Date.parse(`${x}T00:00:00Z`);
  return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === x;
}
/** Días del periodo, contando los dos extremos. */
export function diasDelPeriodo(desde: string, hasta: string): number {
  return Math.round((Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / DIA) + 1;
}

export interface Rango { desde: string; hasta: string }

/**
 * Las fechas de un periodo. Uno desconocido (URL manipulada) se trata como `porDefecto`. Personalizado sin fechas
 * arranca en los últimos 90 días; con fechas, se devuelven TAL CUAL (se validan con `errorDelRango`).
 */
export function rangoDe(periodo: string, desde: string, hasta: string, porDefecto: ClavePeriodo = '90', hoy: string = hoyLocal()): Rango {
  const p = esClavePeriodo(periodo) ? periodo : porDefecto;
  const [a, m] = hoy.split('-').map(Number) as [number, number];
  switch (p) {
    case 'mes': return { desde: `${hoy.slice(0, 7)}-01`, hasta: hoy };
    case 'mes_anterior': {
      const [aa, ma] = m === 1 ? [a - 1, 12] : [a, m - 1];
      const ultimo = new Date(Date.UTC(aa, ma, 0)).getUTCDate();
      const mm = String(ma).padStart(2, '0');
      return { desde: `${String(aa)}-${mm}-01`, hasta: `${String(aa)}-${mm}-${String(ultimo)}` };
    }
    case 'anio': return { desde: `${hoy.slice(0, 4)}-01-01`, hasta: hoy };
    case 'personalizado': return { desde: desde === '' ? sumarDias(hoy, -89) : desde, hasta: hasta === '' ? hoy : hasta };
    case '30': return { desde: sumarDias(hoy, -29), hasta: hoy };
    case '90': return { desde: sumarDias(hoy, -89), hasta: hoy };
  }
}

function sumarDias(iso: string, dias: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + dias * DIA).toISOString().slice(0, 10);
}

/** Por qué el periodo no se puede pedir, o `null` si se puede. `maxDias` = el límite de la pantalla que lo usa. */
export function errorDelRango(r: Rango, maxDias: number): string | null {
  if (!esFechaValida(r.desde) || !esFechaValida(r.hasta)) return 'Indica una fecha «Desde» y una «Hasta» válidas.';
  if (r.desde > r.hasta) return '«Desde» no puede ser posterior a «Hasta».';
  if (diasDelPeriodo(r.desde, r.hasta) > maxDias) return `El periodo admite como mucho ${String(maxDias)} días.`;
  return null;
}

/** Lo que se escribe en la URL al elegir otro periodo: las fechas sólo si es personalizado (y entonces, las de ahora). */
export function cambioDePeriodo(nuevo: string, actual: Rango): Record<'periodo' | 'desde' | 'hasta', string> {
  return nuevo === 'personalizado'
    ? { periodo: 'personalizado', desde: actual.desde, hasta: actual.hasta }
    : { periodo: nuevo, desde: '', hasta: '' };
}
